import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const DEMO_VIEWS = [
    { id: 'agency', label: 'Agenzia', emoji: '🏢' },
    { id: 'agent', label: 'Agente', emoji: '👤' },
    { id: 'soloAgent', label: 'Indipendente', emoji: '⚡' },
];

/**
 * DemoModeBar — Banner fisso visibile agli account demo.
 * Permette di cambiare la "vista" (agenzia / agente / indipendente)
 * e di resettare i dati demo al set iniziale.
 *
 * Props:
 *   view: 'agency' | 'agent' | 'soloAgent'
 *   onViewChange: (newView: string) => void
 *   onReset: () => void
 */
export default function DemoModeBar({ view, onViewChange, onReset }) {
    const [justReset, setJustReset] = useState(false);

    const handleReset = () => {
        onReset();
        setJustReset(true);
        setTimeout(() => setJustReset(false), 2000);
    };

    return (
        <motion.div
            initial={{ y: -60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 280, damping: 22, delay: 0.3 }}
            className="w-full z-50 flex-shrink-0"
            style={{
                background: 'linear-gradient(90deg, rgba(109,40,217,0.18), rgba(168,85,247,0.22), rgba(109,40,217,0.18))',
                borderBottom: '1px solid rgba(139,92,246,0.3)',
                backdropFilter: 'blur(12px)',
            }}
        >
            <div className="max-w-screen-xl mx-auto px-4 py-2 flex flex-wrap items-center justify-between gap-2">

                {/* Left: label */}
                <div className="flex items-center gap-2">
                    <motion.span
                        animate={{ scale: [1, 1.15, 1] }}
                        transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
                        className="text-base"
                    >🎭</motion.span>
                    <span className="text-xs font-semibold" style={{ color: 'rgba(196,181,253,0.85)' }}>
                        Modalità Demo
                    </span>
                    <span className="hidden sm:inline text-xs" style={{ color: 'rgba(139,92,246,0.5)' }}>
                        — Stai visualizzando dati simulati
                    </span>
                </div>

                {/* Center: view switcher */}
                <div className="flex items-center gap-1 rounded-xl p-0.5"
                    style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(139,92,246,0.2)' }}>
                    {DEMO_VIEWS.map(v => (
                        <button
                            key={v.id}
                            onClick={() => onViewChange(v.id)}
                            className="flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium transition-all"
                            style={{
                                background: view === v.id
                                    ? 'linear-gradient(135deg,#6d28d9,#a855f7)'
                                    : 'transparent',
                                color: view === v.id
                                    ? '#fff'
                                    : 'rgba(196,181,253,0.5)',
                                border: 'none',
                                cursor: 'pointer',
                            }}
                        >
                            <span>{v.emoji}</span>
                            <span className="hidden sm:inline">{v.label}</span>
                        </button>
                    ))}
                </div>

                {/* Right: reset button */}
                <button
                    onClick={handleReset}
                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg transition-all"
                    style={{
                        background: justReset ? 'rgba(16,185,129,0.2)' : 'rgba(255,255,255,0.05)',
                        border: `1px solid ${justReset ? 'rgba(16,185,129,0.4)' : 'rgba(139,92,246,0.2)'}`,
                        color: justReset ? '#6ee7b7' : 'rgba(196,181,253,0.6)',
                        cursor: 'pointer',
                    }}
                >
                    <AnimatePresence mode="wait">
                        {justReset ? (
                            <motion.span key="ok" initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
                                ✓ Reset!
                            </motion.span>
                        ) : (
                            <motion.span key="reset" initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
                                🔄 Ripristina
                            </motion.span>
                        )}
                    </AnimatePresence>
                </button>
            </div>
        </motion.div>
    );
}
